<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-background-container">
    <div v-if="report" class="orangehrm-card-container">
      <div class="expensa-head">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ report.title }}
        </oxd-text>
        <status-pill :label="statusLabel" :tone="statusTone" />
        <oxd-button
          display-type="ghost"
          class="orangehrm-sm-button expensa-pdf"
          :label="$t('expensa.action_download_pdf')"
          :disabled="isDownloadingPdf"
          @click="onDownloadPdf"
        />
      </div>
      <oxd-text v-if="report.description" class="expensa-description">
        {{ report.description }}
      </oxd-text>
      <div v-if="report.needsFinanceApproval" class="expensa-warning">
        <oxd-text>{{ $t('expensa.needs_finance_warning') }}</oxd-text>
      </div>
      <oxd-divider />
      <oxd-grid :cols="3" class="orangehrm-full-width-grid">
        <oxd-grid-item v-for="field in summary" :key="field.label">
          <oxd-text class="expensa-label">{{ field.label }}</oxd-text>
          <oxd-text>{{ field.value }}</oxd-text>
        </oxd-grid-item>
      </oxd-grid>
      <oxd-divider />
      <report-actions-bar :report="report" @updated="onUpdated" />
    </div>

    <div v-if="report" class="orangehrm-paper-container expensa-section">
      <div class="orangehrm-header-container">
        <oxd-text tag="h6" class="orangehrm-main-title">
          {{ $t('expensa.items_section') }}
        </oxd-text>
        <oxd-button
          v-if="actions.canEditItems"
          :label="$t('expensa.add_expenses')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
      <div class="orangehrm-container">
        <oxd-card-table
          :items="rows"
          :headers="headers"
          :selectable="false"
          :clickable="true"
          :loading="isLoading"
          row-decorator="oxd-table-decorator-card"
          @click="onClickItem"
        />
      </div>
    </div>

    <div v-if="report" class="orangehrm-card-container expensa-section">
      <oxd-text tag="h6" class="orangehrm-main-title">
        {{ $t('expensa.history') }}
      </oxd-text>
      <ul class="expensa-events">
        <li v-for="event in events" :key="event.id">
          <oxd-text class="expensa-event-title">
            {{ event.label }}
          </oxd-text>
          <oxd-text class="expensa-label">
            {{ event.author }} - {{ event.when }}
          </oxd-text>
          <oxd-text v-if="event.comment">{{ event.comment }}</oxd-text>
        </li>
      </ul>
    </div>

    <preservation-section
      v-if="report"
      :report-id="report.id"
      :report-status="report.status"
    />

    <add-expenses-dialog
      v-if="report"
      ref="addDialog"
      :report-id="report.id"
      :emp-number="report.empNumber"
      @updated="onUpdated"
    />
  </div>
</template>

<script>
import AddExpensesDialog from '@/orangehrmExpensaPlugin/components/AddExpensesDialog.vue';
import PreservationSection from '@/orangehrmExpensaPlugin/components/PreservationSection.vue';
import ReportActionsBar from '@/orangehrmExpensaPlugin/components/ReportActionsBar.vue';
import StatusPill from '@/orangehrmExpensaPlugin/components/StatusPill.vue';
import {expensaApi, errorMessage, fileUrl, pageUrl} from '../util/api';
import {downloadExport} from '../util/download';
import {day, eur, instant, personName} from '../util/format';
import {eventKey, kindKey, reportStatusKey} from '../util/labels';
import {reportActions} from '../util/reportActions';

export default {
  name: 'ExpensaReportDetail',
  components: {
    'add-expenses-dialog': AddExpensesDialog,
    'preservation-section': PreservationSection,
    'report-actions-bar': ReportActionsBar,
    'status-pill': StatusPill,
  },
  props: {
    reportId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      report: null,
      isLoading: true,
      isRemoving: false,
      isDownloadingPdf: false,
    };
  },
  computed: {
    actions() {
      return reportActions(this.report);
    },
    statusLabel() {
      return this.$t(
        reportStatusKey(this.report.status, this.report.awaitingFinance),
      );
    },
    statusTone() {
      return this.report.awaitingFinance ? 'AWAITING' : this.report.status;
    },
    summary() {
      const r = this.report;
      const period = r.month
        ? `${String(r.month).padStart(2, '0')}/${r.fiscalYear}`
        : '—';
      return [
        {label: this.$t('expensa.owner'), value: personName(r.owner)},
        {label: this.$t('expensa.col_period'), value: period},
        {label: this.$t('expensa.total_amount'), value: eur(r.totalEur)},
        {
          label: this.$t('expensa.reimbursable_amount'),
          value: eur(r.reimbursableEur),
        },
        {
          label: this.$t('expensa.col_submitted'),
          value: instant(r.submittedAt),
        },
        {label: this.$t('expensa.approved_on'), value: instant(r.approvedAt)},
        {label: this.$t('expensa.rejected_on'), value: instant(r.rejectedAt)},
        {
          label: this.$t('expensa.reimbursed_on'),
          value: instant(r.reimbursedAt),
        },
        {
          label: this.$t('expensa.payroll_period'),
          value: r.payrollPeriod || '—',
        },
        {
          label: this.$t('expensa.approvers'),
          value: r.approvers.map((a) => personName(a)).join(', ') || '—',
        },
      ];
    },
    rows() {
      return this.report.items.map(({expense: e}) => ({
        id: e.id,
        date: day(e.date),
        kind: this.$t(kindKey(e.kind)),
        category: e.categoryName || '—',
        merchant: e.merchant || e.description || '—',
        amount: eur(e.amountEur),
        flags: (e.policyViolations || [])
          .map(
            (v) =>
              `${this.$t(
                v.severity === 'block'
                  ? 'expensa.severity_block'
                  : 'expensa.severity_warn',
              )}: ${v.message}`,
          )
          .join('; '),
      }));
    },
    headers() {
      const headers = [];
      headers.push(
        {name: 'date', title: this.$t('general.date'), style: {flex: 2}},
        {name: 'kind', title: this.$t('expensa.col_kind'), style: {flex: 2}},
        {
          name: 'category',
          title: this.$t('expensa.col_category'),
          style: {flex: 3},
        },
        {
          name: 'merchant',
          title: this.$t('expensa.col_merchant'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 4},
        },
        {
          name: 'amount',
          title: this.$t('expensa.col_amount_eur'),
          style: {flex: 2},
        },
        {
          name: 'flags',
          title: this.$t('expensa.col_flags'),
          cellType: 'oxd-table-cell-truncate',
          style: {flex: 4},
        },
      );
      if (this.actions.canEditItems) {
        headers.push({
          name: 'actions',
          slot: 'right',
          title: this.$t('general.actions'),
          style: {flex: 2},
          cellType: 'oxd-table-cell-actions',
          cellConfig: {
            remove: {
              onClick: this.onClickRemove,
              component: 'oxd-button',
              props: {
                label: this.$t('expensa.remove_item'),
                displayType: 'text',
                size: 'medium',
              },
            },
          },
        });
      }
      return headers;
    },
    events() {
      return this.report.events.map((e) => ({
        id: e.id,
        label: this.$t(eventKey(e.type)),
        author: e.user || this.$t('expensa.system_user'),
        when: instant(e.createdAt),
        comment: e.comment,
      }));
    },
  },
  beforeMount() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      try {
        const {data} = await expensaApi('/reports').get(this.reportId);
        this.report = data.data;
      } catch (e) {
        this.fail(e);
      } finally {
        this.isLoading = false;
      }
    },
    fail(e) {
      this.$toast.error({
        title: this.$t('general.error'),
        message: errorMessage(e),
      });
    },
    async onDownloadPdf() {
      if (this.isDownloadingPdf) return;
      this.isDownloadingPdf = true;
      try {
        await downloadExport(
          fileUrl(`/expensa/reportPdf/${this.report.id}`),
          `nota-spese-${this.report.id}.pdf`,
          this.$t('expensa.pdf_failed'),
          this.$t('expensa.pdf_session_expired'),
        );
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: e instanceof Error ? e.message : errorMessage(e),
        });
      } finally {
        this.isDownloadingPdf = false;
      }
    },
    onUpdated(detail) {
      this.report = detail;
    },
    onClickAdd() {
      this.$refs.addDialog.showDialog();
    },
    onClickItem(item) {
      window.location.href = pageUrl('viewExpense', item.id);
    },
    async onClickRemove(item) {
      // Il doppio clic su "Togli" non deve lanciare due DELETE.
      if (this.isRemoving) return;
      this.isRemoving = true;
      try {
        const {data} = await expensaApi(
          `/reports/${this.report.id}/items`,
        ).delete(item.id);
        this.report = data.data;
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_item_removed'),
        });
      } catch (e) {
        this.fail(e);
      } finally {
        this.isRemoving = false;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-head {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.expensa-section {
  margin-top: 1rem;
}
.expensa-pdf {
  margin-left: auto;
}
.expensa-label {
  font-size: 0.8rem;
  opacity: 0.7;
}
.expensa-description {
  margin-top: 0.5rem;
}
.expensa-warning {
  margin-top: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.5rem;
  background: #fff3d6;
  color: #8a5a00;
}
.expensa-events {
  list-style: none;
  margin: 0;
  padding: 0;
  li {
    padding: 0.5rem 0;
    border-bottom: 1px solid #e8eaef;
  }
}
.expensa-event-title {
  font-weight: 600;
}
</style>
